import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../systems/util/constants.js';
import { SaveManager } from '../systems/SaveManager.js';
import {
  buildSidebar,
  resolveStartLevelId,
  SIDEBAR_W,
  INK,
  INK_NUM,
  MUTED,
  PAPER_NUM,
  TEXT_RESOLUTION
} from '../systems/ui/chrome.js';
import levels from '../../data/levels.json';

const CONTENT_X = SIDEBAR_W + 24;
const CONTENT_RIGHT = 940;
const CONTENT_W = CONTENT_RIGHT - CONTENT_X;

const DISTRICT_META = {
  'lower-sector': {
    label: 'LOWER SECTOR',
    blurb: 'The wastes. Out of sight, out of mind. But we live here.',
    thumb: [TEXTURE_KEYS.BG_FAR_1, TEXTURE_KEYS.BG_NEAR_1]
  },
  'upper-sector': {
    label: 'UPPER SECTOR',
    blurb: 'Corp towers, clean neon, and turrets watching every rooftop.',
    thumb: [TEXTURE_KEYS.BG_FAR_2, TEXTURE_KEYS.BG_NEAR_3]
  }
};

function formatTime(ms) {
  if (ms == null) return '--:--';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

/** 0-3 stars from real save data: completed + fraction of data chips found. */
function computeStars(state) {
  if (state.bestTimeMs == null) return 0;
  if (!state.chipsTotal) return 3;
  const frac = (state.bestChips ?? 0) / state.chipsTotal;
  if (frac >= 1) return 3;
  if (frac >= 0.5) return 2;
  return 1;
}

function starGlyphs(n) {
  return '★★★'.slice(0, n) + '☆☆☆'.slice(0, 3 - n);
}

export class LevelSelect extends Phaser.Scene {
  constructor() {
    super('LevelSelect');
  }

  create() {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#ffffff');

    this.districtOrder = ['lower-sector', 'upper-sector'];
    this.districtIds = {
      'lower-sector': Object.keys(levels).filter((id) => levels[id].district === 'lower-sector'),
      'upper-sector': Object.keys(levels).filter((id) => levels[id].district === 'upper-sector')
    };

    this._transient = [];
    this._cardRects = {};
    this.activeDistrict = 'lower-sector';
    this.selectedId = this.districtIds[this.activeDistrict][0];

    buildSidebar(this, {
      active: 'levels',
      onNav: (key) => {
        if (key === 'start') this.scene.start('Game', { levelId: resolveStartLevelId(levels) });
      }
    });

    this.add.text(CONTENT_X, 12, '■ LEVEL SELECT', {
      fontFamily: 'monospace',
      fontSize: '13px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    });

    this.tabButtons = {};
    const tabXs = { 'lower-sector': 690, 'upper-sector': 820 };
    for (const district of this.districtOrder) {
      const tab = this.add
        .text(tabXs[district], 17, DISTRICT_META[district].label, {
          fontFamily: 'monospace',
          fontSize: '9px',
          fontStyle: 'bold',
          color: MUTED,
          padding: { x: 8, y: 4 },
          resolution: TEXT_RESOLUTION
        })
        .setInteractive({ useHandCursor: true });
      tab.on('pointerdown', () => this._switchDistrict(district));
      this.tabButtons[district] = tab;
    }

    this.add.rectangle(CONTENT_X + CONTENT_W / 2, 34, CONTENT_W, 1, INK_NUM, 0.25);

    // Detail / stats bar chrome (persists across district + selection changes)
    const barY = 312;
    const barH = 136;
    this.add
      .rectangle(CONTENT_X + CONTENT_W / 2, barY, CONTENT_W, barH, PAPER_NUM, 1)
      .setStrokeStyle(1, INK_NUM);

    this.nameText = this.add.text(CONTENT_X + 12, barY - barH / 2 + 10, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    });
    this.blurbText = this.add.text(CONTENT_X + 12, barY - barH / 2 + 28, '', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: MUTED,
      lineSpacing: 4,
      wordWrap: { width: 216 },
      resolution: TEXT_RESOLUTION
    });

    this.add.text(CONTENT_X + 240, barY - barH / 2 + 10, 'OBJECTIVES', {
      fontFamily: 'monospace',
      fontSize: '8px',
      fontStyle: 'bold',
      color: INK,
      resolution: TEXT_RESOLUTION
    });
    this.objectivesText = this.add.text(CONTENT_X + 240, barY - barH / 2 + 24, '', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: MUTED,
      lineSpacing: 6,
      resolution: TEXT_RESOLUTION
    });

    this.statsText = this.add.text(CONTENT_X + 490, barY - barH / 2 + 10, '', {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: MUTED,
      lineSpacing: 6,
      resolution: TEXT_RESOLUTION
    });

    this.playBtn = this.add
      .text(CONTENT_RIGHT - 12, barY + barH / 2 - 14, '', {
        fontFamily: 'monospace',
        fontSize: '11px',
        fontStyle: 'bold',
        color: '#ffffff',
        backgroundColor: '#141414',
        padding: { x: 14, y: 7 },
        resolution: TEXT_RESOLUTION
      })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    this.playBtn.on('pointerdown', () => {
      if (SaveManager.isUnlocked(this.selectedId)) this.scene.start('Game', { levelId: this.selectedId });
    });

    this._renderDistrict(this.activeDistrict);
  }

  _switchDistrict(district) {
    if (this.activeDistrict === district) return;
    this.activeDistrict = district;
    this.selectedId = this.districtIds[district][0];
    this._renderDistrict(district);
  }

  _renderDistrict(district) {
    for (const obj of this._transient) obj.destroy();
    this._transient = [];
    this._cardRects = {};

    for (const [key, tab] of Object.entries(this.tabButtons)) {
      const active = key === district;
      tab.setColor(active ? '#ffffff' : MUTED);
      tab.setStyle({ backgroundColor: active ? '#141414' : 'transparent' });
    }

    const ids = this.districtIds[district];
    const [farKey, nearKey] = DISTRICT_META[district].thumb;

    const cols = 2;
    const cardW = (CONTENT_W - 12) / cols;
    const cardH = 58;
    const gapY = 8;
    const gridTop = 46;

    ids.forEach((id, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = CONTENT_X + cardW / 2 + col * (cardW + 12);
      const y = gridTop + row * (cardH + gapY) + cardH / 2;

      const state = SaveManager.getLevelState(id);
      const def = levels[id];
      const unlocked = state.unlocked;
      const completed = state.bestTimeMs != null;

      const card = this.add
        .rectangle(x, y, cardW, cardH, PAPER_NUM, 1)
        .setStrokeStyle(1, INK_NUM);
      this._transient.push(card);
      this._cardRects[id] = card;

      const thumbW = 46;
      const thumbH = 46;
      const thumbX = x - cardW / 2 + 8 + thumbW / 2;
      this._addThumbnail(thumbX, y, thumbW, thumbH, farKey, nearKey, !unlocked);

      const textX = x - cardW / 2 + 8 + thumbW + 10;
      const num = String(i + 1).padStart(2, '0');
      const title = this.add.text(textX, y - 15, `${num}  ${unlocked ? def.name : '???'}`, {
        fontFamily: 'monospace',
        fontSize: '9px',
        fontStyle: 'bold',
        color: unlocked ? INK : MUTED,
        resolution: TEXT_RESOLUTION
      });
      this._transient.push(title);

      if (unlocked) {
        const stars = this.add.text(textX, y + 1, starGlyphs(computeStars(state)), {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: INK,
          resolution: TEXT_RESOLUTION
        });
        this._transient.push(stars);
      }

      const badge = this.add
        .text(x + cardW / 2 - 8, y - cardH / 2 + 8, unlocked ? (completed ? 'DONE' : '') : '\u{1F512}', {
          fontFamily: 'monospace',
          fontSize: '8px',
          fontStyle: 'bold',
          color: completed ? '#ffffff' : MUTED,
          backgroundColor: completed ? '#141414' : undefined,
          padding: completed ? { x: 4, y: 2 } : undefined,
          resolution: TEXT_RESOLUTION
        })
        .setOrigin(1, 0);
      this._transient.push(badge);

      if (unlocked) {
        card.setInteractive({ useHandCursor: true });
        card.on('pointerdown', () => this._selectLevel(id));
        card.on('pointerover', () => {
          if (this.selectedId !== id) card.setStrokeStyle(1, 0x555555);
        });
        card.on('pointerout', () => this._refreshCardStyle(id));
      }
    });

    this._selectLevel(this.selectedId);
  }

  _addThumbnail(x, y, w, h, farKey, nearKey, dim) {
    const maskG = this.make.graphics({ x: 0, y: 0, add: false });
    maskG.fillRect(x - w / 2, y - h / 2, w, h);
    const mask = maskG.createGeometryMask();

    const sky = this.add.image(x, y - h / 2, farKey).setOrigin(0.5, 0);
    sky.setScale((h * 1.5) / sky.height);
    sky.setMask(mask);

    const near = this.add.image(x, y + h / 2, nearKey).setOrigin(0.5, 1);
    near.setScale((h * 1.2) / near.height);
    near.setMask(mask);

    if (dim) {
      sky.setAlpha(0.3);
      near.setAlpha(0.3);
    }

    const frame = this.add.rectangle(x, y, w, h).setStrokeStyle(1, INK_NUM, 0.5);

    this._transient.push(maskG, sky, near, frame);
  }

  _refreshCardStyle(id) {
    const card = this._cardRects[id];
    if (!card) return;
    const selected = this.selectedId === id;
    card.setStrokeStyle(selected ? 2 : 1, INK_NUM, 1);
  }

  _selectLevel(id) {
    const prevId = this.selectedId;
    this.selectedId = id;
    if (this._cardRects[prevId]) this._refreshCardStyle(prevId);
    if (this._cardRects[id]) this._refreshCardStyle(id);

    const def = levels[id];
    const state = SaveManager.getLevelState(id);
    const unlocked = state.unlocked;
    const completed = state.bestTimeMs != null;

    this.nameText.setText(unlocked ? def.name : 'LOCKED');
    this.nameText.setColor(unlocked ? INK : MUTED);
    this.blurbText.setText(
      unlocked ? DISTRICT_META[def.district].blurb : 'Complete the previous level to unlock.'
    );

    if (unlocked) {
      this.objectivesText.setText(
        `${completed ? '✓' : '☐'} Complete the level\n` +
          `${state.chipsTotal ? (state.bestChips ?? 0) >= state.chipsTotal ? '✓' : '☐' : '—'} Find all data chips` +
          (state.chipsTotal ? ` (${state.bestChips ?? 0}/${state.chipsTotal})` : '')
      );
      this.statsText.setText(
        `BEST TIME  ${formatTime(state.bestTimeMs)}\nSEED  ${def.seed}` + (def.boss ? `\nBOSS  ${def.boss}` : '')
      );
    } else {
      this.objectivesText.setText('');
      this.statsText.setText('');
    }

    this.playBtn.setText(unlocked ? '▶ START LEVEL' : '🔒 LOCKED');
    this.playBtn.setAlpha(unlocked ? 1 : 0.4);
  }
}
